import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import QuizIcon from '@mui/icons-material/Quiz';
import { testsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const OpenAnswerReviewPage = () => {
  const { t } = useTranslation(['tests', 'common']);
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [submitting, setSubmitting] = useState({});
  const [regenerating, setRegenerating] = useState({});
  const [feedbacks, setFeedbacks] = useState({});
  const [partialPoints, setPartialPoints] = useState({});
  const [expanded, setExpanded] = useState({});

  const isAdmin = user?.userMeta?.roles?.includes('admin') || user?.roles?.includes('admin');
  const isTeacher = user?.userMeta?.roles?.includes('teacher') || user?.roles?.includes('teacher');
  const hasAccess = isAdmin || isTeacher;

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await testsAPI.getPendingReviews({ page, limit: 10 });
      const data = res.data.results || [];
      setResults(data);
      setTotalPages(res.data.pagination?.totalPages || 1);
      // Auto-expand first result
      if (data.length > 0) {
        setExpanded(prev => ({ ...prev, [data[0]._id]: true }));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('tests:openAnswerReview.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleAccordion = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleRegenerateAI = async (resultId, answerIndex) => {
    const key = `${resultId}-${answerIndex}`;
    setRegenerating(prev => ({ ...prev, [key]: true }));
    try {
      await testsAPI.regenerateAIReview(resultId, answerIndex);
      toast.success(t('tests:openAnswerReview.aiUpdated'));
      await fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.message || t('tests:openAnswerReview.aiError'));
    } finally {
      setRegenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleReview = async (resultId, answerIndex, earnedPoints) => {
    const key = `${resultId}-${answerIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      await testsAPI.reviewAnswer(resultId, answerIndex, {
        earnedPoints,
        feedback: feedbacks[key] || null,
      });
      toast.success(earnedPoints > 0 ? t('tests:openAnswerReview.approveSuccess') : t('tests:openAnswerReview.rejectSuccess'));
      await fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.message || t('tests:openAnswerReview.reviewError'));
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (!hasAccess) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('common:errors.accessDenied')}</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        {t('tests:openAnswerReview.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('tests:openAnswerReview.subtitle')}
      </Typography>

      {results.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          {t('tests:openAnswerReview.allReviewed')}
        </Alert>
      ) : (
        results.map((testResult) => {
          const pendingCount = testResult.answers.filter(a => a.reviewStatus === 'pending').length;
          const aiRejectedCount = testResult.answers.filter(a => a.reviewStatus === 'rejected' && !a.reviewedBy).length;
          const totalNeedReview = pendingCount + aiRejectedCount;
          const hasPending = pendingCount > 0;

          return (
            <Accordion
              key={testResult._id}
              expanded={!!expanded[testResult._id]}
              onChange={() => handleAccordion(testResult._id)}
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                border: '1px solid',
                borderColor: hasPending ? 'warning.light' : 'error.light',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: hasPending ? 'warning.50' : 'error.50',
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2, flexWrap: 'wrap' },
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <QuizIcon fontSize="small" color={hasPending ? 'warning' : 'error'} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {testResult.test?.title || t('tests:openAnswerReview.test')}
                    </Typography>
                    <Badge
                      badgeContent={totalNeedReview}
                      color={hasPending ? 'warning' : 'error'}
                      sx={{ ml: 1 }}
                    >
                      <Chip
                        icon={hasPending ? <PendingIcon /> : <ErrorIcon />}
                        label={hasPending
                          ? t('tests:openAnswerReview.pendingChip')
                          : t('tests:openAnswerReview.aiRejectedChip')}
                        color={hasPending ? 'warning' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </Badge>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {testResult.user?.userMeta?.fullName || testResult.user?.username || '—'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled">
                      {new Date(testResult.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3 }}>
                {testResult.answers.map((answer, answerIndex) => {
                  const isAIAutoRejected = answer.reviewStatus === 'rejected' && !answer.reviewedBy;
                  if (answer.reviewStatus !== 'pending' && !isAIAutoRejected) return null;
                  const key = `${testResult._id}-${answerIndex}`;
                  const maxPts = testResult.test?.questions?.[answerIndex]?.points || 1;
                  const currentPts = partialPoints[key] !== undefined ? partialPoints[key] : maxPts;

                  return (
                    <Paper
                      key={answerIndex}
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor: isAIAutoRejected ? 'error.main' : 'warning.main',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Question header */}
                      <Box
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          backgroundColor: isAIAutoRejected ? 'error.50' : 'warning.50',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" color={isAIAutoRejected ? 'error.dark' : 'warning.dark'}>
                          {t('tests:openAnswerReview.question', { number: answerIndex + 1 })}
                        </Typography>
                        {isAIAutoRejected && (
                          <Chip
                            icon={<ErrorIcon />}
                            label={t('tests:openAnswerReview.aiAutoRejectedChip')}
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {maxPts} {t('tests:openAnswerReview.pts')}
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        {/* Question text */}
                        <Typography variant="body1" fontWeight={500} sx={{ mb: 2 }}>
                          {answer.question}
                        </Typography>

                        {/* Student answer */}
                        <Card sx={{ p: 2, mb: 1.5, borderRadius: '8px', backgroundColor: 'action.hover' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('tests:openAnswerReview.studentAnswer')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {answer.userAnswer || t('tests:openAnswerReview.emptyAnswer')}
                          </Typography>
                        </Card>

                        {/* Evaluation criteria */}
                        {answer.correctAnswer && answer.correctAnswer !== 'Open answer' && (
                          <Card sx={{ p: 2, mb: 1.5, borderRadius: '8px', backgroundColor: 'info.50', border: '1px solid', borderColor: 'info.light' }}>
                            <Typography variant="caption" color="info.dark" fontWeight={600}>
                              {t('tests:openAnswerReview.criteria')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {answer.correctAnswer}
                            </Typography>
                          </Card>
                        )}

                        {/* AI suggestion */}
                        {answer.aiSuggestion && (
                          <Card sx={{ p: 2, mb: 1.5, borderRadius: '8px', border: '1px dashed', borderColor: 'primary.main' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <AutoAwesomeIcon fontSize="small" color="primary" />
                              <Typography variant="caption" color="primary" fontWeight={600}>
                                {t('tests:openAnswerReview.aiSuggestion', { score: answer.aiSuggestion.score })}
                              </Typography>
                              <Chip
                                label={answer.aiSuggestion.suggestion === 'approved'
                                  ? t('tests:openAnswerReview.aiApprove')
                                  : t('tests:openAnswerReview.aiReject')}
                                color={answer.aiSuggestion.suggestion === 'approved' ? 'success' : 'error'}
                                size="small"
                                sx={{ ml: 'auto' }}
                              />
                            </Box>
                            <Typography variant="body2">
                              {answer.aiSuggestion.feedback}
                            </Typography>
                          </Card>
                        )}

                        {/* Comment field */}
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder={t('tests:openAnswerReview.commentPlaceholder')}
                          value={feedbacks[key] || ''}
                          onChange={(e) => setFeedbacks(prev => ({ ...prev, [key]: e.target.value }))}
                          sx={{ mb: 1.5 }}
                        />

                        {/* Points + action buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              type="number"
                              size="small"
                              label={t('tests:openAnswerReview.points')}
                              value={currentPts}
                              onChange={(e) => {
                                const v = Math.max(0, Math.min(maxPts, Number(e.target.value)));
                                setPartialPoints(prev => ({ ...prev, [key]: v }));
                              }}
                              slotProps={{ htmlInput: { min: 0, max: maxPts, step: 1 } }}
                              sx={{ width: 90 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              / {maxPts}
                            </Typography>
                          </Box>

                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={submitting[key] ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                            onClick={() => handleReview(testResult._id, answerIndex, currentPts)}
                            disabled={submitting[key] || regenerating[key]}
                          >
                            {t('tests:openAnswerReview.submit')}
                          </Button>

                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={submitting[key] ? <CircularProgress size={14} color="inherit" /> : <ErrorIcon />}
                            onClick={() => handleReview(testResult._id, answerIndex, 0)}
                            disabled={submitting[key] || regenerating[key]}
                          >
                            {t('tests:openAnswerReview.reject')}
                          </Button>

                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={regenerating[key] ? <CircularProgress size={14} /> : <RefreshIcon />}
                            onClick={() => handleRegenerateAI(testResult._id, answerIndex)}
                            disabled={submitting[key] || regenerating[key]}
                          >
                            {t('tests:openAnswerReview.askAIAgain')}
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Container>
  );
};

export default OpenAnswerReviewPage;
